import React from "react";
import { Button, Collapse, Space, Switch, Tooltip, Typography } from "antd";
import { DeleteOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { GRAPH_COLORS, GROUPBY_OPTIONS } from "../constants";
import { usePlotContext } from "./PlotContext";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import type { GraphFormState } from "./plotState";
import {
  GraphTypeStep,
  SelectMetricsStep,
  DateRangeStep,
  PlayersStep,
  AdditionalOptionsStep,
} from "./steps";

const { Panel } = Collapse;
const { Text } = Typography;

interface StepDef {
  key: string;
  label: string;
  children: React.ReactNode;
}

function buildStepItems(
  graph: GraphFormState,
  index: number,
  handler: (patch: Partial<GraphFormState>) => void,
  isPartOfStack: boolean,
): StepDef[] {
  const isStacked = graph.stacked;

  const allSteps: StepDef[] = [
    { key: "1", label: "Plot Type", children: <GraphTypeStep graph={graph} graphIndex={index} onChange={handler} /> },
    { key: "2", label: "Select Metrics", children: <SelectMetricsStep graph={graph} graphIndex={index} onChange={handler} /> },
    { key: "3", label: "Date Range", children: <DateRangeStep graph={graph} graphIndex={index} onChange={handler} /> },
    { key: "4", label: "Filters", children: <PlayersStep graph={graph} graphIndex={index} onChange={handler} /> },
    { key: "5", label: "Additional Options", children: <AdditionalOptionsStep graph={graph} onChange={handler} isPartOfStack={isPartOfStack} /> },
  ];

  // Stacked series inherit the date range from Plot 1, so the Date Range step stays hidden.
  return isStacked
    ? allSteps.filter((s) => ["1", "2", "4", "5"].includes(s.key))
    : allSteps;
}

export function PlotConfigAccordion() {
  const { state, dispatch, metrics } = usePlotContext();
  const isTimeGroupBy =
    GROUPBY_OPTIONS.find((o) => o.value === state.groupBy)?.section === "time";

  return (
    <>
      {/* Plot-level configuration */}
      <Collapse style={{ borderRadius: 0 }}>
        <Panel
          key="plot-config"
          header={
            <Space size={6}>
              <SettingOutlined />
              <Text strong style={{ fontSize: 13 }}>Plot Configuration</Text>
            </Space>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Space size={8} align="center">
              <Text style={{ fontSize: 12, minWidth: 56 }}>Title</Text>
              <SlicerTokenInput
                size="small"
                placeholder="Sprint Distance"
                value={state.title}
                onChange={(title) => dispatch({ type: "SET_TITLE", title })}
              />
            </Space>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text style={{ fontSize: 12 }}>Show legend</Text>
              <Switch
                size="small"
                checked={state.showLegend}
                onChange={(v) => dispatch({ type: "SET_SHOW_LEGEND", value: v })}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text style={{ fontSize: 12 }}>Horizontal (inverted)</Text>
              <Switch
                size="small"
                checked={state.inverted}
                onChange={(v) => dispatch({ type: "SET_INVERTED", value: v })}
              />
            </div>
            {isTimeGroupBy && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Tooltip title="Draw every day, week or month in the date range, even when it has no data. Averages, trendlines and reference lines ignore the empty slots.">
                  <Text style={{ fontSize: 12 }}>Show all dates in range</Text>
                </Tooltip>
                <Switch
                  size="small"
                  checked={state.showAllDatesInRange}
                  onChange={(v) => dispatch({ type: "SET_SHOW_ALL_DATES_IN_RANGE", value: v })}
                />
              </div>
            )}
          </div>
        </Panel>
      </Collapse>

      {/* Per-graph accordions */}
      <Collapse
        accordion
        activeKey={state.expandedGraphKey}
        onChange={(key) => dispatch({ type: "SET_EXPANDED_GRAPH_KEY", key })}
        style={{ borderRadius: 0, borderTop: "none" }}
      >
        {state.graphs.map((graph, index) => {
          const color = (graph.enableColorOverride && graph.colorOverride) || GRAPH_COLORS[index % GRAPH_COLORS.length];
          const metricName = metrics.find((m) => m.id === graph.selectedMetricId)?.name;
          const handler = (patch: Partial<GraphFormState>) =>
            dispatch({ type: "UPDATE_GRAPH", index, patch });

          return (
            <Panel
              key={`graph-${graph.id}`}
              header={
                <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                  <span
                    style={{
                      width: 9, height: 9, borderRadius: "50%",
                      background: color, flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 13 }}>Plot {index + 1}</Text>
                    {metricName && (
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                        {metricName}
                      </Text>
                    )}
                  </div>
                </div>
              }
              extra={
                state.graphs.length > 1 ? (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch({ type: "REMOVE_GRAPH", index });
                    }}
                    style={{ marginRight: -4 }}
                  />
                ) : undefined
              }
            >
              <Collapse
                accordion
                defaultActiveKey={["1"]}
                className="awm-inner-steps"
              >
                {buildStepItems(graph, index, handler, graph.stacked || state.graphs.some((g) => g.stackOnGraphId === graph.id)).map((step) => (
                  <Panel
                    key={step.key}
                    header={
                      <Space size={8}>
                        <span className="awm-step-number">{step.key}</span>
                        <Text strong style={{ fontSize: 12 }}>{step.label}</Text>
                      </Space>
                    }
                  >
                    {step.children}
                  </Panel>
                ))}
              </Collapse>
            </Panel>
          );
        })}
      </Collapse>

      <Button
        type="text"
        icon={<PlusOutlined />}
        onClick={() => dispatch({ type: "ADD_GRAPH" })}
        style={{ width: "100%", fontWeight: 600, color: "#1677ff" }}
      >
        Add Plot
      </Button>
    </>
  );
}
